/**
 * Toughened glass, broken: the crumbs it bursts into.
 *
 * A bus shelter's panes are toughened glass, which does not break into
 * blades but bursts all at once into thousands of small, blunt cubes. Each
 * crumb here stands for a handful of them: a point that flies, tumbles,
 * bounces low off the ground (a crumb of glass hardly bounces), slides a
 * little and lies there, glittering, where it stopped. Hundreds are cheap: a
 * crumb at rest is never touched again. Kept in flat arrays, the oldest
 * crumb given up first when there are too many.
 */

const GRAVITY = 9.81;
/** Coming down faster than this, m/s, a crumb bounces; slower, it settles. */
const BOUNCES = 0.8;
/** How much of its fall it bounces back with, and how much of its slide it keeps per bounce. */
const BOUNCE = 0.25;
const SCUFF = 0.6;
/** How hard it slides to a stop on the ground, m/s². */
const SLIDE = 6;

/** The ground's height under a point near height `y`, or undefined where it is not known yet. */
export type ShardGround = (x: number, z: number, y: number) => number | undefined;

export class ShardField {
  /** Position, velocity and turn (radians about x, y, z) of each crumb, three to a crumb; its size, metres. */
  readonly p: Float32Array;
  readonly v: Float32Array;
  readonly turn: Float32Array;
  readonly spin: Float32Array;
  readonly size: Float32Array;
  /** 1 once it lies still. */
  readonly resting: Uint8Array;
  /** How many crumbs there are, and which slot the next one takes. */
  count = 0;
  private next = 0;

  constructor(readonly capacity: number) {
    this.p = new Float32Array(capacity * 3);
    this.v = new Float32Array(capacity * 3);
    this.turn = new Float32Array(capacity * 3);
    this.spin = new Float32Array(capacity * 3);
    this.size = new Float32Array(capacity);
    this.resting = new Uint8Array(capacity);
  }

  /** A new crumb at (x, y, z) flying at (vx, vy, vz), `size` metres across, spinning at `spin` rad/s. */
  add(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, spin: number): number {
    const i = this.next;
    this.next = (this.next + 1) % this.capacity;
    this.count = Math.min(this.capacity, this.count + 1);
    this.p.set([x, y, z], i * 3);
    this.v.set([vx, vy, vz], i * 3);
    this.turn.set([0, 0, 0], i * 3);
    this.spin.set([spin, spin * 0.7, -spin * 0.5], i * 3);
    this.size[i] = size;
    this.resting[i] = 0;
    return i;
  }

  /** Move every crumb in the air or sliding on by `dt`; true for each that moved, in `moved`. */
  step(dt: number, ground: ShardGround, moved?: Uint8Array): void {
    for (let i = 0; i < this.count; i++) {
      if (moved !== undefined) moved[i] = 0;
      if (this.resting[i] === 1) continue;
      const k = i * 3;
      this.v[k + 1] -= GRAVITY * dt;
      this.p[k] += this.v[k] * dt;
      this.p[k + 1] += this.v[k + 1] * dt;
      this.p[k + 2] += this.v[k + 2] * dt;
      for (let a = 0; a < 3; a++) this.turn[k + a] += this.spin[k + a] * dt;
      if (moved !== undefined) moved[i] = 1;
      const h = ground(this.p[k], this.p[k + 2], this.p[k + 1]);
      if (h === undefined || this.p[k + 1] > h) continue;
      this.p[k + 1] = h;
      if (this.v[k + 1] < -BOUNCES) {
        // A low bounce, and it loses some of its slide to the ground.
        this.v[k + 1] = -this.v[k + 1] * BOUNCE;
        this.v[k] *= SCUFF;
        this.v[k + 2] *= SCUFF;
        for (let a = 0; a < 3; a++) this.spin[k + a] *= 0.5;
        continue;
      }
      // On the ground: sliding to a stop, flat.
      this.v[k + 1] = 0;
      const speed = Math.hypot(this.v[k], this.v[k + 2]);
      const slower = Math.max(0, speed - SLIDE * dt);
      if (slower < 0.02) {
        this.v[k] = this.v[k + 2] = 0;
        this.resting[i] = 1;
        // Lying on a face.
        this.turn[k] = 0;
        this.turn[k + 2] = 0;
        continue;
      }
      this.v[k] *= slower / speed;
      this.v[k + 2] *= slower / speed;
      for (let a = 0; a < 3; a++) this.spin[k + a] *= Math.exp(-dt * 6);
    }
  }

  /** Move every crumb by (dx, dz): the scene's origin moved. */
  shift(dx: number, dz: number): void {
    for (let i = 0; i < this.count; i++) {
      this.p[i * 3] += dx;
      this.p[i * 3 + 2] += dz;
    }
  }
}

/**
 * Where a pane's crumbs go when a vehicle bursts it, from a crumb's distance
 * `near` to where it was struck, metres, the vehicle's velocity (scene x, z)
 * and the push along the hit. Near the hit they are carried along with the
 * vehicle, most of its speed, and up; further off the pane just falls, a
 * little outward. `dice` are three rolls in [0, 1). Velocity x, y, z.
 */
export function burst(near: number, vehicle: { x: number; z: number }, push: { x: number; z: number }, dice: [number, number, number]): [number, number, number] {
  const carried = Math.max(0, 1 - near / 1.6);
  const keep = carried * (0.45 + dice[0] * 0.6);
  const spread = 0.6 + carried * 1.5;
  return [
    vehicle.x * keep + push.x * (dice[1] * 1.2) + (dice[2] - 0.5) * spread,
    (dice[0] * 1.4 + carried * 1.5) * (0.4 + dice[1]),
    vehicle.z * keep + push.z * (dice[1] * 1.2) + (dice[1] - 0.5) * spread,
  ];
}
